<!-- src\views\components\EditPlaylist.vue -->
<template>
  <div class="card mb-4">
    <div class="card-header pb-0 d-flex justify-content-between align-items-center">
      <h5>Edit Playlist</h5>
    </div>

    <div class="card-body px-3 pt-2 pb-4">
      <form @submit.prevent="editTask" class="container">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label for="name">Playlist Name</label>
              <input v-model="newEvent.name" type="text" class="form-control" id="name" required />
              <span v-if="errors.name" class="error">{{ errors.name }}</span>
            </div>
          </div>

          <!-- MULTI-SELECT BRANCHES DROPDOWN -->
          <div class="col-md-5">
            <div class="form-group">
              <label for="branches">Branches</label>
              <multiselect
                v-model="newEvent.selectedBranches"
                :options="availableBranches"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select Branches"
                label="branch_name"
                track-by="id"
              ></multiselect>
              <span v-if="errors.selectedBranches" class="error">{{ errors.selectedBranches }}</span>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="status">Status</label>
              <select class="form-control" id="status" v-model="newEvent.status" required>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- EMERGENCY BROADCAST FIELD -->
          <div class="col-md-3">
            <div class="form-group">
              <label for="isEmergency">Emergency Broadcast</label>
              <div class="form-check form-switch mt-1">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="isEmergency" 
                  v-model="newEvent.is_emergency_broadcast" 
                  :true-value="1" 
                  :false-value="0"
                >
                <label class="form-check-label" for="isEmergency">
                  {{ newEvent.is_emergency_broadcast == 1 ? 'Yes' : 'No' }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Media List -->
        <div v-if="selectedMedia.length" class="selected-media mt-4">
          <h6 class="text-center mb-3">Selected Media (Drag to reorder)</h6>
          <div class="row">
            <div v-for="(media, index) in selectedMedia" :key="'media-'+index" class="col-md-3 mb-4"
                 draggable="true"
                 @dragstart="dragStart(index, $event)"
                 @drop="drop(index)"
                 @dragover.prevent
                 @dragenter.prevent
                 @dragend="dragEnd"
                 :class="{'dragging': draggedItemIndex === index}">
                 
              <div class="media-card cursor-grab d-flex flex-column h-100">
                <!-- Media Preview Section -->
                <div class="preview-container">
                  <div class="media-tag" :class="media.type.startsWith('image') ? 'image-tag' : 'video-tag'">
                    {{ media.type.startsWith('image') ? "Image" : "Video" }}
                  </div>
                  
                  <template v-if="media.type.startsWith('image')">
                    <img :src="media.url" class="media-preview" draggable="false" />
                  </template>
                  <template v-else-if="media.type.startsWith('video')">
                    <video class="media-preview">
                      <source :src="media.url" :type="media.type" />
                    </video>
                  </template>
                  
                  <span class="remove-btn" @click.stop="removeMedia(index)">×</span>
                </div>

                <!-- Dynamic Media Details Section -->
                <div class="media-details p-2 text-start d-flex flex-column justify-content-center">
                  <p class="mb-1 text-xs text-dark text-truncate" :title="media.media_name">
                    <strong>Name:</strong> {{ media.media_name || 'Loading...' }}
                  </p>
                  <p class="mb-1 text-xs text-dark">
                    <strong>Type:</strong> {{ media.type.startsWith('image') ? 'Image' : 'Video' }}
                  </p>
                  <p class="mb-0 text-xs text-dark">
                    <strong>Time:</strong> {{ media.display_time || 0 }}s
                  </p>
                </div>
              </div>
              
            </div>
          </div>
        </div>

        <!-- Buttons Section -->
        <div class="text-center mt-3 d-flex justify-content-center align-items-center" style="gap: 15px;">
          <router-link :to="{
            path: '/edit-media-gallery',
            query: { 
              selectedMedia: JSON.stringify(selectedMedia), 
              playlistId: this.$route.params.id,
              selectedBranches: JSON.stringify(newEvent.selectedBranches),
              playlist_name: newEvent.name, 
              status: newEvent.status,
              is_emergency_broadcast: newEvent.is_emergency_broadcast
            }
          }" class="btn btn-success mb-0">
            Open Media Gallery
          </router-link>

          <!-- Preview Button -->
          <button type="button" v-if="selectedMedia.length" @click="startPreview" class="btn btn-info mb-0">
            Preview Playlist
          </button>
        </div>

        <span v-if="errors.selectedMedia" class="error d-block text-center mt-2">{{ errors.selectedMedia }}</span>
        
        <!-- Submit Button -->
        <div class="row">
          <div class="col-md-12 text-center">
            <button type="submit" class="btn btn-primary mt-3"
              style="background-color: #141727; color:white; border:2px solid #141727">
              Update Playlist
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Preview Modal -->
    <div v-if="isPreviewPlaying" class="preview-overlay">
      <div class="preview-modal">
        <button class="close-preview-btn" @click="stopPreview">&times;</button>
        
        <div v-if="currentMedia" class="media-container w-100 h-100 d-flex justify-content-center align-items-center position-relative">
          
          <!-- Image -->
          <img v-if="currentMedia.type.startsWith('image')" :src="currentMedia.url" class="full-preview-media" />
          
          <!-- Video -->
          <video v-else-if="currentMedia.type.startsWith('video')" 
                 :key="currentMedia.url"
                 :src="currentMedia.url" 
                 class="full-preview-media" 
                 autoplay 
                 muted 
                 controls>
          </video>
          
          <!-- Progress Indicator -->
          <div class="position-absolute bottom-0 start-0 text-white p-3 font-weight-bold" style="text-shadow: 1px 1px 3px rgba(0,0,0,0.8);">
             Playing: {{ currentMedia.media_name }} ({{ currentMedia.display_time }}s)
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import axios from 'axios';
// import PlayListDataService from "../../services/PlayListDataService.js"; // COMMENTED TO PREVENT HTTP CALLS
import MediaDataService from "../../services/MediaDataService.js"; 
import Swal from 'sweetalert2';
import { io } from "socket.io-client"; // NEW: Socket Client

export default {
  name: "EditPlaylist",
  components: {
    Multiselect
  },
  data() {
    return {
      socket: null, // NEW: Holder for socket instance
      selectedMedia: [],
      mediaCatalog: {}, 
      availableBranches: [],
      newEvent: {
        name: "",
        selectedBranches: [],
        status: "1",
        is_emergency_broadcast: 0,
      },
      errors: {},
      
      draggedItemIndex: null,
      isPreviewPlaying: false,
      currentPreviewIndex: 0,
      previewTimer: null,
    };
  },
  computed: {
    currentMedia() {
      return this.selectedMedia[this.currentPreviewIndex];
    }
  },
  async created() {
    // Wait for available branches to load first to prevent race condition
    await this.getAllBranches();
    this.fetchAllMediaCatalog();

    // Check query params or fetch playlist
    const selected = this.$route.query.selectedMedia;
    if (selected) {
      this.selectedMedia = JSON.parse(selected);
      this.enrichMediaDetails();
    }
    
    if (this.$route.query.playlist_name) {
      this.newEvent.name = this.$route.query.playlist_name;
    }

    if (this.$route.query.selectedBranches) {
      this.newEvent.selectedBranches = JSON.parse(this.$route.query.selectedBranches);
    }

    if (this.$route.query.status) {
      this.newEvent.status = this.$route.query.status;
    }

    if (this.$route.query.is_emergency_broadcast !== undefined) {
      this.newEvent.is_emergency_broadcast = parseInt(this.$route.query.is_emergency_broadcast);
    }

    if (this.$route.params.id) {
      this.fetchPlaylist(this.$route.params.id);
    }
  },
  mounted() {
    // --- NEW: INITIALIZE SOCKET CONNECTION ---
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    const baseUrl = process.env.VUE_APP_API_BASE_URL;
    const url = new URL(baseUrl);
    const socketUrl = `${url.protocol}//${url.host}`;

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'], // Force WebSocket to fix Session ID issues
      upgrade: false,
      reconnection: true
    });

    this.socket.on('connect', () => {
      console.log("✅ Socket connected (Edit Page):", this.socket.id);
    });

    // LISTEN FOR RESPONSE
    this.socket.on('playlist_response', (res) => {
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: res.message || 'Playlist updated successfully via Webhook!',
          confirmButtonText: 'OK'
        }).then(() => {
          this.$router.push('/playlist');
        });
      } else {
        Swal.fire('Error', res.message || 'Failed to update playlist', 'error');
      }
    });
  },
  methods: {
    dragStart(index, event) {
      this.draggedItemIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    drop(index) {
      if (this.draggedItemIndex !== null && this.draggedItemIndex !== index) {
        const draggedItem = this.selectedMedia.splice(this.draggedItemIndex, 1)[0];
        this.selectedMedia.splice(index, 0, draggedItem);
      }
      this.draggedItemIndex = null;
    },
    dragEnd() {
      this.draggedItemIndex = null;
    },

    startPreview() {
      if (this.selectedMedia.length === 0) return;
      this.isPreviewPlaying = true;
      this.currentPreviewIndex = 0;
      this.playCurrentMedia();
    },
    playCurrentMedia() {
      if (this.currentPreviewIndex >= this.selectedMedia.length) {
        this.stopPreview(); 
        return;
      }
      
      const media = this.currentMedia;
      const displayTimeMs = (media.display_time || 5) * 1000; 

      this.previewTimer = setTimeout(() => {
        this.nextMedia();
      }, displayTimeMs);
    },
    nextMedia() {
      clearTimeout(this.previewTimer);
      this.currentPreviewIndex++;
      this.playCurrentMedia();
    },
    stopPreview() {
      this.isPreviewPlaying = false;
      clearTimeout(this.previewTimer);
      this.currentPreviewIndex = 0;
    },

    fetchAllMediaCatalog() {
      MediaDataService.getAll()
        .then(response => {
          const mediaList = Array.isArray(response.data) ? response.data : (response.data.data || []);
          
          mediaList.forEach(item => {
            const resolvedName = item.name || item.media_name || item.title || item.file_name || 'Unknown';
            
            this.mediaCatalog[item.id] = {
              media_name: resolvedName,
              display_time: item.display_time || 5
            };
          });
          this.enrichMediaDetails(); 
        })
        .catch(e => console.log('Error fetching media catalog:', e));
    },
    
    enrichMediaDetails() {
      if (Object.keys(this.mediaCatalog).length === 0 || this.selectedMedia.length === 0) return;
      
      this.selectedMedia = this.selectedMedia.map(media => {
        const catalogInfo = this.mediaCatalog[media.id];
        
        if (catalogInfo) {
          return {
            ...media,
            media_name: catalogInfo.media_name !== 'Unknown' ? catalogInfo.media_name : (media.media_name || 'Unknown'),
            display_time: catalogInfo.display_time || media.display_time || 5
          };
        }
        return media;
      });
    },

    removeMedia(index) {
      this.selectedMedia.splice(index, 1);
    },
    
    getAllBranches() {
      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');

      return axios.get(`${baseUrl}/v1/admin/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        this.availableBranches = response.data.map(branch => ({
          id: Number(branch.id),
          branch_name: branch.branch_name
        }));
        return this.availableBranches;
      })
      .catch(e => {
        console.log('Error fetching branches:', e);
        return [];
      });
    },
    
    fetchPlaylist(id) {
      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');

      axios.get(`${baseUrl}/v1/admin/playlist-master/edit/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => {
          const playlist = response.data.playlist;
          const branchesFromBackend = response.data.branches || [];
          const selectedFromQuery = this.$route.query.selectedMedia ? JSON.parse(this.$route.query.selectedMedia) : [];

          // FIXED: Logic for Media Loading
          if (selectedFromQuery.length > 0) {
            this.selectedMedia = selectedFromQuery;
          } else if (playlist.playlistDetails) {
            this.selectedMedia = playlist.playlistDetails.map(media => ({
              id: media.media_id,
              url: media.media_url,
              // Convert database type (1 or 2) back to mime-type for template logic
              type: media.type == 1 ? 'image/jpeg' : 'video/mp4',
              media_name: media.media_name || 'Loading...'
            }));
          }

          this.enrichMediaDetails();

          // FIXED: Logic for Branches Loading (Multiselect needs objects with branch_name)
          const selectedBranchesFromQuery = this.$route.query.selectedBranches ? JSON.parse(this.$route.query.selectedBranches) : null;

          let selectedBranches = [];
          if (selectedBranchesFromQuery && selectedBranchesFromQuery.length > 0) {
            selectedBranches = selectedBranchesFromQuery;
          } else if (branchesFromBackend.length > 0) {
            // Priority: use the branches returned specifically for this playlist from the backend
            selectedBranches = branchesFromBackend.map(b => ({ 
                id: Number(b.id), 
                branch_name: b.branch_name 
            }));
          } else {
            // Fallback: Use the raw branch_ids from the playlist object and match against availableBranches
            let rawBranchIds = playlist.branch_ids || [];
            if (typeof rawBranchIds === 'string') {
                try { rawBranchIds = JSON.parse(rawBranchIds); } catch(e) { rawBranchIds = []; }
            }
            const idList = Array.isArray(rawBranchIds) ? rawBranchIds.map(Number) : [];
            selectedBranches = this.availableBranches.filter(b => idList.includes(Number(b.id)));
          }

          const selectedPlaylistName = this.$route.query.playlist_name ?? playlist.playlist_name;
          const selectedStatus = this.$route.query.status ?? playlist.status.toString();
          const selectedEmergency = this.$route.query.is_emergency_broadcast ?? playlist.is_emergency_broadcast;

          this.newEvent = {
            name: selectedPlaylistName,
            selectedBranches: selectedBranches,
            status: selectedStatus,
            is_emergency_broadcast: selectedEmergency ?? 0,
          };
        })
        .catch(error => console.log('Error fetching playlist:', error));
    },
    
editTask() {
      if (!this.newEvent.name || !this.newEvent.selectedBranches || this.newEvent.selectedBranches.length === 0) {
        this.errors = {
          name: this.newEvent.name ? '' : 'Playlist Name is required.',
          selectedBranches: (this.newEvent.selectedBranches && this.newEvent.selectedBranches.length) ? '' : 'At least one Branch must be selected.',
        };
        return;
      }

      if (this.selectedMedia.length === 0) {
        this.errors.selectedMedia = 'At least one media must be selected.';
        return;
      } else {
        this.errors.selectedMedia = '';
      }

      const branchIds = this.newEvent.selectedBranches.map(b => Number(b.id));

      const playlistData = {
        playlist_name: this.newEvent.name,
        branch_ids: branchIds,
        status: parseInt(this.newEvent.status), 
        is_emergency_broadcast: this.newEvent.is_emergency_broadcast,
        media_id: this.selectedMedia.map(media => ({
          id: media.id,
          media_url: media.url,
          type: (media.type === 'image/jpeg' || media.type.startsWith('image')) ? '1' : '2'
        })),
      };

      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');

      // Ensure the URL matches your backend routes/admin.js definition
      axios.post(`${baseUrl}/v1/admin/playlist-master/update/${this.$route.params.id}`, playlistData, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Playlist updated successfully!',
            confirmButtonText: 'OK'
          }).then(() => {
            this.$router.push('/playlist');
          });
      })
      .catch(error => {
          console.error("Update Error:", error);
          const message = error.response?.data?.message || 'Route not found (404) or Server Error';
          Swal.fire('Error', message, 'error');
      });
    

      /* --- ORIGINAL SOCKET EMIT COMMENTED OUT ---
      if (this.socket && this.socket.connected) {
          console.log("📤 Emitting update_playlist_socket:", playlistData);
          this.socket.emit('update_playlist_socket', playlistData);
      } else {
          Swal.fire('Connection Error', 'Socket is disconnected. Refreshing page...', 'error').then(() => window.location.reload());
      }
      */
    }
  },
  beforeUnmount() {
    clearTimeout(this.previewTimer); 
  }
};
</script>

<style scoped>
.error {
  color: red;
  font-size: 12px;
}

.selected-media {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.cursor-grab {
  cursor: grab;
}
.cursor-grab:active {
  cursor: grabbing;
}
.dragging {
  opacity: 0.5;
  border: 2px dashed #007bff;
  border-radius: 10px;
}

.media-card {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}
.media-card:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.preview-container {
  position: relative;
  width: 100%;
  height: 140px;
  background-color: #000;
}

.media-details {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  flex-grow: 1;
}

.media-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: red;
  color: white;
  border: none;
  font-size: 16px;
  font-weight: bold;
  padding: 0;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.3s;
}
.remove-btn:hover {
  background: darkred;
}

.media-tag {
    position: absolute;
    bottom: 5px;
    left: 5px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: bold;
    border-radius: 4px;
    color: white;
    z-index: 2;
}
.image-tag { background: rgba(0, 123, 255, 0.9); }
.video-tag { background: rgba(255, 69, 0, 0.9); }

.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.preview-modal {
  position: relative;
  width: 90vw;
  height: 85vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.close-preview-btn {
  position: absolute;
  top: -30px;
  right: 10px;
  background: transparent;
  color: white;
  border: none;
  font-size: 45px;
  cursor: pointer;
  z-index: 10000;
  transition: color 0.3s;
}

.close-preview-btn:hover {
  color: #ff4500;
}

.full-preview-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
}
</style>